import {
  ComponentType,
  createContext,
  Dispatch,
  LazyExoticComponent,
  MutableRefObject,
  RefObject,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';

import { TClip, TClipDeclaration, TClipLogo, TClipOcr, TClipPeople } from '@/features/clips/types';
import {
  TClipPresence,
  TSegment,
  TSegmentDeclaration,
  TSegmentLogo,
  TSegmentOcr,
  TSegmentPeople,
  TSegmentPresence,
  getClipPresenceKind,
  getSegmentPresenceKind,
} from '@/features/segments/types';
import { usePresenceLease } from '@/hooks/usePresenceLease';
import { useUnderMinDesktopWidth } from '@/hooks/useUnderMinDesktopWidth';
import { useWebSocket } from '@/providers/websocket';
import { TRect } from '@/types';
import { getAbsoluteDateFromPlayerTime } from '@/utils/hh-mm-ss-into-number';
import { dispatchByKind } from '@/utils/objects';

import { CUT_FORM_DEFAULT_VALUES } from '../components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitCuts/constants';
import { TBroadcastScheduleProgramBodySegmentToolkitCutForm } from '../components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitCuts/types';
import { FrameActionType } from '../components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import {
  EClipToolkit,
  ESegmentToolkit,
  TBroadcastScheduleProgramBodySegmentToolkitAdvertisementItem,
  TBroadcastScheduleProgramBodySegmentToolkitMentionItem,
} from '../components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/types';
import { useSyncProgramFilters } from '../components/BroadcastScheduleProgramHeaderFilters/hooks/useSyncProgramFilters';
import { useScrollElementRef } from '../hooks/useScrollElementRef';
import { useToolkitState } from '../hooks/useToolkitState';
import { BoxRow, TToolkitComponentProps } from '../types';

export type TLogoData = TSegmentLogo | TClipLogo | null;
export type TPersonaData = TSegmentPeople | TClipPeople | null;
export type TPresenceData = TLogoData | TPersonaData | null;

type TFormBox =
  | 'logo'
  | 'people'
  | 'rotulo'
  | 'mention'
  | 'advertisement'
  | 'cut'
  | 'declaration'
  | null;

/** How much of the cut list is loaded, so the panel can ask for the next batch as it scrolls. */
export type TClipsPaging = {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  /** Changes when the query restarts, which is the only time the loaded list is discarded. */
  listKey: string;
  /** The rows on screen belong to the previous result, held while the new one is on its way. */
  isRestarting: boolean;
};

/** The endpoint behind the cut list: the programme's cuts, or the active segment's. */
export type TCutsListSource = {
  kind: 'program' | 'segment';
  id: string;
};

export const DEFAULT_CLIPS_PAGING: TClipsPaging = {
  hasNextPage: false,
  isFetchingNextPage: false,
  fetchNextPage: () => {},
  listKey: '',
  isRestarting: false,
};

export type TVisibleBroadcastProgramCards = {
  segments: boolean;
  segmentDetail: boolean;
  segmentToolkit: boolean;
  filters: boolean;
};

export type TProgramNavigationState = {
  id: string;
  gte: string;
  lte: string;
};

export type TBroadcastScheduleProgramContextState = {
  visibleBroadcastProgramCards: TVisibleBroadcastProgramCards;
  activeSegment: TSegment | null;
  totalActiveCards: number;
  isHeaderVisible: boolean;
  hoveredId: ESegmentToolkit | null;
  pinnedId: ESegmentToolkit | null;
  toolkitComponents: Record<
    ESegmentToolkit,
    LazyExoticComponent<ComponentType<TToolkitComponentProps>>
  >;
  toolkitClipComponents: Record<
    EClipToolkit,
    LazyExoticComponent<ComponentType<TToolkitComponentProps>>
  >;
  visibleBox: BoxRow | null;
  formBox: TFormBox;
  logoData: TLogoData | null;
  personaData: TSegmentPeople | null;
  rotuloData: TSegmentOcr | null;
  mentionData: TBroadcastScheduleProgramBodySegmentToolkitMentionItem | null;
  advertisementData: TBroadcastScheduleProgramBodySegmentToolkitAdvertisementItem | null;
  cutData: TClip | null;
  playerVideoCurrentTime: number;
  playerVideoDuration: number;
  playerVideoIsPlaying: boolean;
  detailCardRef: RefObject<HTMLDivElement>;
  prevDrawerRectRef: MutableRefObject<TRect | null>;
  drawerLogo: boolean;
  drawerRect: TRect | null;
  toolkitCardRegionRef: RefObject<HTMLDivElement>;
  selectedMention: string | null;
  textToHighlight: string | null;
  drawingFor: 'logo' | 'people' | null;
  videoPlayerRef: RefObject<HTMLDivElement>;
  totalSecondsItems: number | null;
  pendingSeekTime: number | null;
  compactUis: boolean;
  segmentItems: TSegment[];
  declarationData: TSegmentDeclaration | null;
  activeClip: TClip | null;
  clipItems: TClip[];
  rotuloClipData: TClipOcr | null;
  personaClipData: TClipPeople | null;
  declarationClipData: TClipDeclaration | null;
  isParrillaSegments: boolean;
  isGlobalView: boolean;
  globalViewSegments: TSegment[];
  globalViewOnPreview:
    | ((presence: TSegmentLogo | TSegmentPeople, frame?: FrameActionType) => void)
    | null;
  programNavigationState: TProgramNavigationState | null;
  videoControlRef: RefObject<{
    seekToSecond: (second: number) => void;
    play: () => void;
    stop: () => void;
  } | null>;
  cutForm: ReturnType<typeof useForm<TBroadcastScheduleProgramBodySegmentToolkitCutForm>>;
  clipBySegmentItems: TClip[];
  totalClipsBySegment: number;
  clipsPaging: TClipsPaging;
  setClipsPaging: Dispatch<SetStateAction<TClipsPaging>>;
  cutsListSource: TCutsListSource | null;
  setCutsListSource: Dispatch<SetStateAction<TCutsListSource | null>>;
  setTotalClipsBySegment: Dispatch<SetStateAction<number>>;
  isLoadingClipsBySegment: boolean;
  setIsLoadingClipsBySegment: Dispatch<SetStateAction<boolean>>;
  selectedMentionTime: Date | null;
  segmentItemsRefs: MutableRefObject<{ [key: string]: HTMLElement | null }>;
  handleDateTimeSegment: (
    form: UseFormReturn<any>,
    type: 'start_datetime' | 'end_datetime'
  ) => void;
  handleDateTimeClip: (form: UseFormReturn<any>, type: 'start_datetime' | 'end_datetime') => void;
  setSelectedMentionTime: Dispatch<SetStateAction<Date | null>>;
  setRotuloClipData: Dispatch<SetStateAction<TClipOcr | null>>;
  setClipBySegmentItems: Dispatch<SetStateAction<TClip[]>>;
  setIsParrillaSegments: Dispatch<SetStateAction<boolean>>;
  setIsGlobalView: Dispatch<SetStateAction<boolean>>;
  setGlobalViewSegments: Dispatch<SetStateAction<TSegment[]>>;
  setGlobalViewOnPreview: (
    fn: ((presence: TSegmentLogo | TSegmentPeople, frame?: FrameActionType) => void) | null
  ) => void;
  setProgramNavigationState: Dispatch<SetStateAction<TProgramNavigationState | null>>;
  handleResetVisibleBroadcastProgramCards: () => void;
  setDeclarationClipData: Dispatch<SetStateAction<TClipDeclaration | null>>;
  setPersonaClipData: Dispatch<SetStateAction<TClipPeople | null>>;
  setClipItems: Dispatch<SetStateAction<TClip[]>>;
  setActiveClip: Dispatch<SetStateAction<TClip | null>>;
  setDeclarationData: Dispatch<SetStateAction<TSegmentDeclaration | null>>;
  setSegmentItems: Dispatch<SetStateAction<TSegment[]>>;
  setCompactUis: Dispatch<SetStateAction<boolean>>;
  setPendingSeekTime: Dispatch<SetStateAction<number | null>>;
  setTotalSecondsItems: Dispatch<SetStateAction<number | null>>;
  setDrawingFor: Dispatch<SetStateAction<'logo' | 'people' | null>>;
  setTextToHighlight: Dispatch<SetStateAction<string | null>>;
  setSelectedMention: Dispatch<SetStateAction<string | null>>;
  setDrawerRect: Dispatch<SetStateAction<TRect | null>>;
  onDrawnRect: (rect: TRect) => void;
  setDrawerLogo: Dispatch<SetStateAction<boolean>>;
  setPlayerVideoDuration: Dispatch<SetStateAction<number>>;
  setPlayerVideoCurrentTime: Dispatch<SetStateAction<number>>;
  setPlayerVideoIsPlaying: Dispatch<SetStateAction<boolean>>;
  setCutData: Dispatch<SetStateAction<TClip | null>>;
  setAdvertisementData: Dispatch<
    SetStateAction<TBroadcastScheduleProgramBodySegmentToolkitAdvertisementItem | null>
  >;
  setMentionData: Dispatch<
    SetStateAction<TBroadcastScheduleProgramBodySegmentToolkitMentionItem | null>
  >;
  setRotuloData: Dispatch<SetStateAction<TSegmentOcr | null>>;
  setPersonaData: (value: TSegmentPeople | null) => void;
  setLogoData: (value: TLogoData | null) => void;
  setFormBox: Dispatch<SetStateAction<TFormBox>>;
  setVisibleBox: Dispatch<SetStateAction<BoxRow | null>>;
  handleMouseEnter: (id: ESegmentToolkit) => void;
  handleMouseLeave: (id: ESegmentToolkit) => void;
  handleTooltikClick: (id: ESegmentToolkit) => void;
  setIsHeaderVisible: Dispatch<SetStateAction<boolean>>;
  setActiveSegment: Dispatch<SetStateAction<TSegment | null>>;
  setVisibleBroadcastProgramCards: Dispatch<SetStateAction<TVisibleBroadcastProgramCards>>;
  drawnFormReset: () => void;
  isSegmentPlayerMode: boolean;
  getDateTimeCurrentSegment: (playerVideoCurrentTime: number) => Date | undefined;
  getDateTimeCurrentClip: (playerVideoCurrentTime: number) => Date | undefined;
  getDateTimeCurrentPresence: (playerVideoCurrentTime: number) => Date | undefined;
  goToPresence: (presence: TSegmentPresence, seconds: number, seekVideo?: boolean) => void;
  goToClipPresence: (presence: TClipPresence, seconds: number, seekVideo?: boolean) => void;
  clearPresence: () => void;
  presenceData: TPresenceData;
};

export const BroadcastScheduleProgramContext = createContext<TBroadcastScheduleProgramContextState>(
  {} as TBroadcastScheduleProgramContextState
);

export const BroadcastScheduleProgramContextProvider = ({ children }) => {
  useSyncProgramFilters();
  const { underMinDesktopWith } = useUnderMinDesktopWidth();
  const DEFAULT_VISIBLE_BROADCAST_PROGRAM_CARDS = {
    segments: !underMinDesktopWith,
    segmentDetail: false,
    segmentToolkit: !underMinDesktopWith,
    filters: true,
  };
  const [isHeaderVisible, setIsHeaderVisible] = useState<boolean>(true);
  const [activeSegment, setActiveSegment] = useState<TSegment | null>(null);
  const [activeClip, setActiveClip] = useState<TClip | null>(null);
  const { connectSegment, disconnectSegment, connectClip, disconnectClip } = useWebSocket();

  usePresenceLease(activeSegment?.id, connectSegment, disconnectSegment);
  usePresenceLease(activeClip?.id, connectClip, disconnectClip);

  const [compactUis, setCompactUis] = useState<boolean>(false);
  const [isParrillaSegments, setIsParrillaSegments] = useState<boolean>(false);
  const [isGlobalView, setIsGlobalView] = useState<boolean>(false);
  const [globalViewSegments, setGlobalViewSegments] = useState<TSegment[]>([]);
  const [globalViewOnPreview, _setGlobalViewOnPreview] = useState<
    ((presence: TSegmentLogo | TSegmentPeople, frame?: FrameActionType) => void) | null
  >(null);
  const setGlobalViewOnPreview = useCallback(
    (fn: ((presence: TSegmentLogo | TSegmentPeople, frame?: FrameActionType) => void) | null) => {
      _setGlobalViewOnPreview(() => fn);
    },
    []
  );
  const [programNavigationState, setProgramNavigationState] =
    useState<TProgramNavigationState | null>(null);
  const [visibleBroadcastProgramCards, setVisibleBroadcastProgramCards] =
    useState<TVisibleBroadcastProgramCards>(DEFAULT_VISIBLE_BROADCAST_PROGRAM_CARDS);

  const handleResetVisibleBroadcastProgramCards = useCallback(() => {
    setVisibleBroadcastProgramCards(DEFAULT_VISIBLE_BROADCAST_PROGRAM_CARDS);
  }, [DEFAULT_VISIBLE_BROADCAST_PROGRAM_CARDS]);

  const detailCardRef = useRef<HTMLDivElement>(null);

  const totalActiveCards = useMemo(() => {
    return Object.values(visibleBroadcastProgramCards).filter(Boolean).length;
  }, [visibleBroadcastProgramCards]);

  useEffect(() => {
    if (activeSegment || activeClip) {
      setVisibleBroadcastProgramCards(prev => ({ ...prev, segmentDetail: true }));
    } else {
      setVisibleBroadcastProgramCards(prev => ({
        ...prev,
        segmentDetail: false,
        segments: !underMinDesktopWith,
        segmentToolkit: !underMinDesktopWith,
      }));
    }
  }, [activeSegment, activeClip]);

  useEffect(() => {
    setVisibleBroadcastProgramCards(prev => ({
      ...prev,
      segments: !underMinDesktopWith,
      segmentToolkit: !underMinDesktopWith,
    }));
  }, [underMinDesktopWith]);

  /* Toolkit */
  const {
    hoveredId,
    pinnedId,
    toolkitComponents,
    toolkitClipComponents,
    handleMouseEnter,
    handleMouseLeave,
    handleTooltikClick,
  } = useToolkitState({
    activeSegment,
    activeClip,
    isParrillaSegments,
    isGlobalView,
    setVisibleBroadcastProgramCards,
  });

  const { segmentItemsRefs } = useScrollElementRef({
    selectedItemId: activeSegment?.id || null,
  });

  const isSegmentPlayerMode = !!activeClip && pinnedId === ESegmentToolkit.CORTES;

  const [logoData, _setLogoData] = useState<TLogoData>(null);
  const [personaData, _setPersonaData] = useState<TSegmentPeople | null>(null);

  const setLogoData = useCallback((value: TLogoData) => {
    _setLogoData(value);
    if (value !== null) _setPersonaData(null);
  }, []);
  const setPersonaData = useCallback((value: TSegmentPeople | null) => {
    _setPersonaData(value);
    if (value !== null) _setLogoData(null);
  }, []);

  /* Logo*/
  const [visibleBox, setVisibleBox] = useState<BoxRow | null>(null);
  const [formBox, setFormBox] = useState<TFormBox>(null);

  /* Persona */
  const [personaClipData, setPersonaClipData] = useState<TClipPeople | null>(null);
  /* Rotulo */
  const [rotuloData, setRotuloData] = useState<TSegmentOcr | null>(null);
  const [rotuloClipData, setRotuloClipData] = useState<TClipOcr | null>(null);
  /* Mention */
  const [mentionData, setMentionData] =
    useState<TBroadcastScheduleProgramBodySegmentToolkitMentionItem | null>(null);
  /* Adversitement */
  const [advertisementData, setAdvertisementData] =
    useState<TBroadcastScheduleProgramBodySegmentToolkitAdvertisementItem | null>(null);
  /* Cut */
  const [cutData, setCutData] = useState<TClip | null>(null);
  const cutForm = useForm<TBroadcastScheduleProgramBodySegmentToolkitCutForm>({
    mode: 'all',
    defaultValues: CUT_FORM_DEFAULT_VALUES,
  });
  /* Declaration */
  const [declarationData, setDeclarationData] = useState<TSegmentDeclaration | null>(null);
  const [declarationClipData, setDeclarationClipData] = useState<TClipDeclaration | null>(null);
  /* Drawer logo */
  const [drawerLogo, setDrawerLogo] = useState<boolean>(false);
  const [drawerRect, setDrawerRect] = useState<TRect | null>(null);
  const [drawingFor, setDrawingFor] = useState<'logo' | 'people' | null>(null);

  const onDrawnRect = (rect: TRect) => {
    setDrawerRect(rect);
  };

  /* Video */
  const [playerVideoCurrentTime, setPlayerVideoCurrentTime] = useState(0);
  const [playerVideoDuration, setPlayerVideoDuration] = useState(0);
  const [playerVideoIsPlaying, setPlayerVideoIsPlaying] = useState(false);
  const [pendingSeekTime, setPendingSeekTime] = useState<number | null>(null);
  const videoControlRef = useRef<{
    seekToSecond: (second: number) => void;
    play: () => void;
    stop: () => void;
  } | null>(null);

  /* SegmentList */
  const [totalSecondsItems, setTotalSecondsItems] = useState<number | null>(null);

  /* ToolkitItemRef */
  const toolkitCardRegionRef = useRef<HTMLDivElement>(null);
  /* Mention in logos */
  const [selectedMention, setSelectedMention] = useState<string | null>(null);
  const [selectedMentionTime, setSelectedMentionTime] = useState<Date | null>(null);
  const [textToHighlight, setTextToHighlight] = useState<string | null>(null);

  /* Drawer Form Reset */
  const prevDrawerRectRef = useRef<typeof drawerRect | null>(null);
  const drawnFormReset = useCallback(() => {
    setFormBox(null);
    setLogoData(null);
    setDrawerLogo(false);
    setDrawerRect(null);
    setVisibleBox(null);
    setSelectedMention(null);
    setSelectedMentionTime(null);
  }, [setFormBox, setLogoData, setDrawerLogo, setDrawerRect]);
  /* VideoDivPlayerRef */
  const videoPlayerRef = useRef<HTMLDivElement>(null);

  /* SegmentItems */
  const [segmentItems, setSegmentItems] = useState<TSegment[]>([]);
  const [clipBySegmentItems, setClipBySegmentItems] = useState<TClip[]>([]);
  const [totalClipsBySegment, setTotalClipsBySegment] = useState<number>(0);
  const [clipsPaging, setClipsPaging] = useState<TClipsPaging>(DEFAULT_CLIPS_PAGING);
  const [cutsListSource, setCutsListSource] = useState<TCutsListSource | null>(null);
  const [isLoadingClipsBySegment, setIsLoadingClipsBySegment] = useState<boolean>(false);
  /* ClipItems */
  const [clipItems, setClipItems] = useState<TClip[]>([]);

  const getDateTimeCurrentSegment = (playerVideoCurrentTime: number) => {
    if (!activeSegment?.start_datetime) return;

    return getAbsoluteDateFromPlayerTime(activeSegment.start_datetime, playerVideoCurrentTime);
  };

  const getDateTimeCurrentClip = (playerVideoCurrentTime: number) => {
    const baseDateTime = isSegmentPlayerMode
      ? activeSegment?.start_datetime
      : activeClip?.start_datetime;

    if (!baseDateTime) return;

    return getAbsoluteDateFromPlayerTime(baseDateTime, playerVideoCurrentTime);
  };

  const getDateTimeCurrentPresence = (playerVideoCurrentTime: number) =>
    activeClip
      ? getDateTimeCurrentClip(playerVideoCurrentTime)
      : getDateTimeCurrentSegment(playerVideoCurrentTime);

  // Every caller marks a field at the current player position; none of them wants
  // to clear one. Writing the undefined these getters return when there is no
  // base instant would wipe a mark the reviewer had already set.
  const handleDateTimeSegment = (
    form: UseFormReturn<any>,
    type: 'start_datetime' | 'end_datetime'
  ) => {
    const newDateTime = getDateTimeCurrentSegment(playerVideoCurrentTime);
    if (!newDateTime) return;

    form.setValue(type, newDateTime);
  };

  const handleDateTimeClip = (
    form: UseFormReturn<any>,
    type: 'start_datetime' | 'end_datetime'
  ) => {
    const newDateTime = getDateTimeCurrentClip(playerVideoCurrentTime);
    if (!newDateTime) return;

    form.setValue(type, newDateTime);
  };

  const segmentPresenceHandlers: {
    logo: (p: TSegmentPresence) => void;
    people: (p: TSegmentPresence) => void;
  } = {
    logo: presence => setLogoData(presence as TSegmentLogo),
    people: presence => setPersonaData(presence as TSegmentPeople),
  };

  const clipPresenceHandlers: {
    logo: (p: TClipPresence) => void;
    people: (p: TClipPresence) => void;
  } = {
    logo: presence => setLogoData(presence as TClipLogo),
    people: presence => setPersonaClipData(presence as TClipPeople),
  };

  const clearPresence = () => {
    setVisibleBox(null);
    _setLogoData(null);
    _setPersonaData(null);
    setPersonaClipData(null);
  };

  const goToPresence = (presence: TSegmentPresence, seconds: number, seekVideo: boolean = true) => {
    dispatchByKind(segmentPresenceHandlers, presence, getSegmentPresenceKind);

    setVisibleBox({
      bbox: presence?.parameters.bbox || [0, 0, 0, 0],
      frame: seconds,
      entity_label: presence?.entity_label || '',
    });

    if (seekVideo) {
      videoControlRef.current?.seekToSecond(seconds);
    }
  };

  const goToClipPresence = (
    presence: TClipPresence,
    seconds: number,
    seekVideo: boolean = true
  ) => {
    dispatchByKind(clipPresenceHandlers, presence, getClipPresenceKind);

    setVisibleBox({
      bbox: presence?.parameters.bbox || [0, 0, 0, 0],
      frame: seconds,
      entity_label: presence?.entity_label || '',
    });

    if (seekVideo) {
      videoControlRef.current?.seekToSecond(seconds);
    }
  };

  const presenceData = logoData || personaData || personaClipData;

  const state = {
    activeClip,
    activeSegment,
    advertisementData,
    clipBySegmentItems,
    clipsPaging,
    clipItems,
    cutsListSource,
    compactUis,
    cutData,
    cutForm,
    declarationClipData,
    declarationData,
    detailCardRef,
    drawerLogo,
    drawerRect,
    drawingFor,
    drawnFormReset,
    formBox,
    getDateTimeCurrentSegment,
    getDateTimeCurrentClip,
    getDateTimeCurrentPresence,
    handleDateTimeClip,
    handleDateTimeSegment,
    handleMouseEnter,
    handleMouseLeave,
    handleResetVisibleBroadcastProgramCards,
    handleTooltikClick,
    hoveredId,
    isHeaderVisible,
    isLoadingClipsBySegment,
    isGlobalView,
    globalViewSegments,
    globalViewOnPreview,
    isParrillaSegments,
    isSegmentPlayerMode,
    logoData,
    mentionData,
    onDrawnRect,
    pendingSeekTime,
    personaClipData,
    personaData,
    pinnedId,
    playerVideoCurrentTime,
    playerVideoDuration,
    playerVideoIsPlaying,
    prevDrawerRectRef,
    programNavigationState,
    rotuloClipData,
    rotuloData,
    segmentItems,
    segmentItemsRefs,
    selectedMention,
    selectedMentionTime,
    setActiveClip,
    setActiveSegment,
    setAdvertisementData,
    setClipBySegmentItems,
    setClipsPaging,
    setClipItems,
    setCutsListSource,
    setCompactUis,
    setCutData,
    setDeclarationClipData,
    setDeclarationData,
    setDrawerLogo,
    setDrawerRect,
    setDrawingFor,
    setFormBox,
    setIsHeaderVisible,
    setIsLoadingClipsBySegment,
    setIsParrillaSegments,
    setIsGlobalView,
    setGlobalViewSegments,
    setGlobalViewOnPreview,
    setProgramNavigationState,
    setLogoData,
    setMentionData,
    setPendingSeekTime,
    setPersonaClipData,
    setPersonaData,
    setPlayerVideoCurrentTime,
    setPlayerVideoDuration,
    setPlayerVideoIsPlaying,
    setRotuloClipData,
    setRotuloData,
    setSegmentItems,
    setSelectedMention,
    setSelectedMentionTime,
    setTextToHighlight,
    setTotalClipsBySegment,
    setTotalSecondsItems,
    setVisibleBox,
    setVisibleBroadcastProgramCards,
    textToHighlight,
    toolkitCardRegionRef,
    toolkitClipComponents,
    toolkitComponents,
    totalActiveCards,
    totalClipsBySegment,
    totalSecondsItems,
    videoControlRef,
    videoPlayerRef,
    visibleBox,
    visibleBroadcastProgramCards,
    goToPresence,
    goToClipPresence,
    clearPresence,
    presenceData,
  };

  return (
    <BroadcastScheduleProgramContext.Provider value={state}>
      {children}
    </BroadcastScheduleProgramContext.Provider>
  );
};

export const useBroadcastScheduleProgram = () =>
  useContext<TBroadcastScheduleProgramContextState>(BroadcastScheduleProgramContext);
